<script>
    import { onMount } from 'svelte';
    import { getApiUrl } from '../api';
    import { authStore } from '../authStore';
    import { authFetch } from '../authService';
    import { t, currentLang } from '../i18nStore';
    import { Link } from 'svelte-routing';
    import CategorySelectorModal from './CategorySelectorModal.svelte';
    import CreditCardSelectorModal from '../components/AdvancedCreditCardSelectorModal.svelte'; 
    import PaymentMethodSelectorModal from './PaymentMethodSelectorModal.svelte';
    import StoreSelectorModal from '../components/StoreSelectorModal.svelte'; 

    export let categories = []; 
    let storeName = '';
    export let targetSpending = '';

    let ownedCards = [];
    let ownedPaymentMethods = [];
    let isLoading = false;
    let recommendation = null;
    let error = '';
    let currentUserId = '';
    let showCategoryModal = false;
    let showCreditCardModal = false;
    let showPaymentMethodModal = false;
    let showStoreModal = false;

    let locationAware = false;
    let userLat = null;
    let userLng = null;
    let locationLoading = false;
    let locationError = '';

    function toggleLocationAware() {
        if (!locationAware) {
            if (!navigator.geolocation) {
                locationError = $t('geo.not_supported');
                return;
            }
            locationLoading = true;
            locationError = '';
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    userLat = pos.coords.latitude;
                    userLng = pos.coords.longitude;
                    locationAware = true;
                    locationLoading = false;
                },
                (err) => {
                    console.warn("Geolocation error:", err);
                    locationError = $t('geo.permission_denied');
                    locationAware = false;
                    locationLoading = false;
                },
                { enableHighAccuracy: true, timeout: 10000 }
            );
        } else {
            locationAware = false;
            userLat = null;
            userLng = null;
            locationError = '';
        }
    }

    onMount(async () => {
        if ($authStore.user) {
            const userId = $authStore.user.idTokenClaims?.oid || $authStore.user.localAccountId;
            try {
                const res = await fetch(getApiUrl(`/users/profile/${userId}`));
                if (res.ok) {
                    const profile = await res.json();
                    if (profile) {
                        ownedCards = profile.ownedCreditCards || [];
                        ownedPaymentMethods = profile.ownedPaymentMethods || [];
                    }
                }
            } catch (e) {
                console.error("Failed to load profile for AI", e);
            }
        }
    });

    function getCurrentDay() {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return days[new Date().getDay()];
    }

    function handleCategoryConfirm(event) {
        categories = event.detail.selected;
        showCategoryModal = false;
    }

    function handleCreditCardConfirm(event) { 
        ownedCards = event.detail.selected; 
        showCreditCardModal = false; 
    }

    function handlePaymentMethodConfirm(event) { 
        ownedPaymentMethods = event.detail.selected; 
        showPaymentMethodModal = false; 
    }

    function handleStoreConfirm(e) {
        storeName = '';
        if (e.detail.selected && e.detail.selected.length > 0) {
            storeName = e.detail.selected[0]; // Take the first selected store 
        }
        showStoreModal = false;
    }

    async function getRecommendation() {
        error = '';
        recommendation = null;
        currentUserId = '';

        if ((!categories || categories.length === 0) && !storeName) { 
            error = $t('ai.error_mandatory'); 
            return; 
        }

        if (!targetSpending || parseFloat(targetSpending) <= 0) {
            error = $t('ai.error_target_spending');
            return;
        }

        if (ownedCards.length === 0 && ownedPaymentMethods.length === 0) { 
            error = $t('ai.error_no_methods'); 
            return; 
        }

        if ($authStore.user) {
            currentUserId = $authStore.user.idTokenClaims?.oid || $authStore.user.localAccountId;
        }

        isLoading = true;

        try {
            let langString = 'English';
            if ($currentLang === 'zh-HK') langString = 'Traditional Chinese';
            if ($currentLang === 'ja') langString = 'Japanese';

            const localDate = new Date();
            const year = localDate.getFullYear();
            const month = String(localDate.getMonth() + 1).padStart(2, '0');
            const day = String(localDate.getDate()).padStart(2, '0');
            const currentDateStr = `${year}-${month}-${day}`;

            const normalizedStoreName = storeName ? storeName.replace(/\s+/g, ' ').trim() : '';

            const payload = {
                categories: categories,
                storeName: normalizedStoreName,
                targetSpending: parseFloat(targetSpending),
                ownedCards: ownedCards,
                ownedPaymentMethods: ownedPaymentMethods,
                currentDay: getCurrentDay(),
                currentDate: currentDateStr,
                language: langString,
                userId: currentUserId
            };

            if (locationAware && userLat != null && userLng != null) {
                payload.locationAware = true;
                payload.latitude = userLat;
                payload.longitude = userLng;
                payload.radiusMeters = 1000;
            }

            const res = await fetch(getApiUrl('/promotions/recommend'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || $t('ai.error_failed'));

            recommendation = data;
        } catch (e) {
            error = e.message;
        } finally {
            isLoading = false;
        }
    }
</script>

<CategorySelectorModal 
    bind:showModal={showCategoryModal} 
    preSelectedCategories={categories} 
    on:confirm={handleCategoryConfirm} 
    on:close={() => showCategoryModal = false} 
/>
<CreditCardSelectorModal 
    bind:showModal={showCreditCardModal} 
    preSelectedCards={ownedCards} 
    on:confirm={handleCreditCardConfirm} 
    on:close={() => showCreditCardModal = false} 
/>
<PaymentMethodSelectorModal 
    bind:showModal={showPaymentMethodModal} 
    preSelectedMethods={ownedPaymentMethods} 
    on:confirm={handlePaymentMethodConfirm} 
    on:close={() => showPaymentMethodModal = false} 
/>
<StoreSelectorModal 
    bind:showModal={showStoreModal} 
    preSelectedStores={storeName ? [storeName] : []} 
    on:confirm={handleStoreConfirm} 
    on:close={() => showStoreModal = false} 
/>

<div class="ai-box">
    <div class="ai-header">
        <span class="icon">✨</span> 
        <h3>{$t('ai.title') || 'AI Smart Recommendation'}</h3>
    </div>
    
    <p class="desc">{$t('ai.desc') || 'Tell us how much you plan to spend, and our AI will calculate which of your cards gives you the maximum reward right now.'}</p>

    <div class="input-grid">
        <div class="field">
            <label>{$t('ai.store_name') || 'Store Name'}</label>
            <div class="input-with-btn">
                <input type="text" bind:value={storeName} placeholder={$t('ai.store_placeholder') || 'e.g. Starbucks'} />
                <button type="button" class="btn-icon-search" on:click={() => showStoreModal = true} title="Browse Stores">🔍</button>
            </div>
        </div>
        <div class="field">
            <label>{$t('ai.categories') || 'Categories'}</label>
            <button type="button" class="btn-select" on:click={() => showCategoryModal = true}>
                {categories.length > 0 ? categories.join(', ') : ($t('ai.select_categories') || 'Select Categories')}
            </button>
        </div>

        <div class="field">
            <label>{$t('ai.target_spending') || 'Target Spending ($) *'}</label>
            <input type="number" bind:value={targetSpending} min="1" step="0.01" placeholder="e.g. 500" />
        </div>

        <div class="field">
            <label>{$t('ai.using_cards') || 'Using Cards:'}</label>
            <button type="button" class="btn-select" on:click={() => showCreditCardModal = true}>
                {ownedCards.length > 0 ? `${ownedCards.length} ${$t('search.selected') || 'Selected'}` : ($t('search.select_cards') || 'Select Cards')}
            </button>
        </div>  
            
        <div class="field">
            <label>{$t('ai.using_payment_methods') || 'Using Payment Methods:'}</label>
            <button type="button" class="btn-select" on:click={() => showPaymentMethodModal = true}>
                {ownedPaymentMethods.length > 0 ? `${ownedPaymentMethods.length} ${$t('search.selected') || 'Selected'}` : ($t('search.select_methods') || 'Select Methods')}
            </button>
        </div>

        <div class="field location-toggle-field">
            <label class="toggle-container">
                <input type="checkbox" checked={locationAware} on:change={toggleLocationAware} disabled={locationLoading} />
                <span class="toggle-slider"></span>
                <span class="toggle-text">
                    {$t('geo.location_aware')}
                    <span class="beta-tag">{$t('geo.beta')}</span>
                </span>
            </label>
            {#if locationLoading}
                <span class="loc-badge loading">⏳ {$t('geo.locating')}</span>
            {:else if locationAware && userLat != null}
                <span class="loc-badge active">📍 {$t('geo.location_ready')}</span>
            {/if}
            {#if locationError}
                <div class="loc-error-msg">⚠️ {locationError}</div>
            {/if}
        </div>

        <button class="btn-recommend" on:click={getRecommendation} disabled={isLoading}>
            {isLoading ? ($t('ai.thinking') || 'Thinking...') : ($t('ai.get_recommendation') || 'Get Recommendation')}
        </button>

    </div>

    {#if error}
        <div class="error-msg">{error}</div>
    {/if}

    {#if recommendation}
        <!-- Check if no deals were found at all -->
        {#if (!recommendation.store_specific_deal?.recommended_promotion_id && !recommendation.category_specific_deal?.recommended_promotion_id)}
            <div class="result-card default-deal">
                <p class="explanation">{recommendation.explanation || ($t('ai.no_suitable') || 'No suitable promotion found for this spending scenario.')}</p>
            </div>
        {:else}
            <div class="deals-grid">
                <!-- Store Specific Deal -->
                {#if recommendation.store_specific_deal?.recommended_promotion_id}
                    <div class="result-card store-deal">
                        <div class="deal-badge">{$t('ai.store_deal')}</div>
                        {#if recommendation.store_specific_deal.nearest_location}
                            <div class="deal-location-badge">
                                📍 {recommendation.store_specific_deal.nearest_location.placeName || recommendation.store_specific_deal.nearest_location.storeName || ''}
                                ({recommendation.store_specific_deal.nearest_location.distanceMeters < 1000 ? `${Math.round(recommendation.store_specific_deal.nearest_location.distanceMeters)}m` : `${(recommendation.store_specific_deal.nearest_location.distanceMeters / 1000).toFixed(1)}km`} {$t('geo.away')})
                            </div>
                        {/if}
                        <h4>
                            <Link to={`/promotions/detail/${recommendation.store_specific_deal.recommended_promotion_id}`} class="highlight">
                                {recommendation.store_specific_deal.recommended_promotion_title}
                            </Link>
                        </h4>
                        <p class="explanation">{recommendation.store_specific_deal.explanation}</p>
                        <div class="reward-estimate">
                            {$t('ai.estimated_reward') || 'Estimated Reward:'} <strong>${parseFloat(recommendation.store_specific_deal.estimated_reward).toFixed(2)}</strong>
                        </div>
                    </div>
                {/if}

                <!-- Category Specific Deal -->
                {#if recommendation.category_specific_deal?.recommended_promotion_id}
                    <div class="result-card category-deal">
                        <div class="deal-badge category-badge">{$t('ai.category_deal')}</div>
                        {#if recommendation.category_specific_deal.nearest_location}
                            <div class="deal-location-badge">
                                📍 {recommendation.category_specific_deal.nearest_location.placeName || recommendation.category_specific_deal.nearest_location.storeName || ''}
                                ({recommendation.category_specific_deal.nearest_location.distanceMeters < 1000 ? `${Math.round(recommendation.category_specific_deal.nearest_location.distanceMeters)}m` : `${(recommendation.category_specific_deal.nearest_location.distanceMeters / 1000).toFixed(1)}km`} {$t('geo.away')})
                            </div>
                        {/if}
                        <h4>
                            <Link to={`/promotions/detail/${recommendation.category_specific_deal.recommended_promotion_id}`} class="highlight">
                                {recommendation.category_specific_deal.recommended_promotion_title}
                            </Link>
                        </h4>
                        <p class="explanation">{recommendation.category_specific_deal.explanation}</p>
                        <div class="reward-estimate">
                            {$t('ai.estimated_reward') || 'Estimated Reward:'} <strong>${parseFloat(recommendation.category_specific_deal.estimated_reward).toFixed(2)}</strong>
                        </div>
                    </div>
                {/if}
            </div>

            <!-- AI Reasoning Dropdown -->
            {#if recommendation.step_by_step_reasoning}
                <div class="reasoning-box">
                    <details>
                        <summary>{$t('ai.view_reasoning')}</summary>
                        <p>{recommendation.step_by_step_reasoning}</p>
                    </details>
                </div>
            {/if}
        {/if}
    {/if}
</div>

<style>
    .ai-box { background: linear-gradient(145deg, #f0f7ff, #ffffff); border: 1px solid #d0e3ff; border-radius: 12px; padding: 1.5rem; margin-bottom: 2rem; box-shadow: 0 4px 15px rgba(0, 123, 255, 0.05); }
    .ai-header { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; }
    .ai-header h3 { margin: 0; color: #0056b3; font-size: 1.2rem; }
    .icon { font-size: 1.5rem; }
    .desc { color: #555; font-size: 0.95rem; margin-bottom: 1.5rem; }

    .input-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 1.5rem; }
    .field label { display: block; font-size: 0.85rem; font-weight: bold; color: #444; margin-bottom: 4px; }
    
    input { padding: 0.6rem; border: 1px solid #ccc; border-radius: 6px; font-size: 1rem; width: 100%; box-sizing: border-box; }

    .input-with-btn { display: flex; align-items: stretch; gap: 0; }
    .input-with-btn input { flex: 1; border-top-right-radius: 0; border-bottom-right-radius: 0; }
    .btn-icon-search { padding: 0 0.8rem; background: #e2e8f0; border: 1px solid #ccc; border-left: none; border-top-right-radius: 6px; border-bottom-right-radius: 6px; cursor: pointer; transition: background 0.2s; }
    .btn-icon-search:hover { background: #cbd5e1; }
    
    .btn-select {
        padding: 0.6rem;
        border: 1px solid #ccc;
        border-radius: 6px;
        font-size: 0.95rem;
        background-color: #f8f9fa;
        color: #333;
        cursor: pointer;
        text-align: left;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        width: 100%;
        box-sizing: border-box;
    }
    .btn-select:hover {
        background-color: #e9ecef;
    }
    
    .btn-recommend { background: #007bff; color: white; border: none; padding: 0.7rem 1.5rem; border-radius: 6px; font-weight: bold; cursor: pointer; transition: all 0.2s; height: 38px; align-self: end; }
    .btn-recommend:hover:not(:disabled) { background: #0056b3; transform: translateY(-1px); }
    .btn-recommend:disabled { background: #a0c4ff; cursor: not-allowed; }

    .error-msg { margin-top: 1rem; color: #d9534f; font-size: 0.9rem; font-weight: 500; }
    
    .deals-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 1.5rem;
        margin-top: 1.5rem;
    }

    .result-card { background: white; padding: 1.2rem; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); animation: fadeIn 0.4s ease-out; position: relative; }
    .default-deal { border-left: 4px solid #6c757d; margin-top: 1.5rem; }
    .store-deal { border-top: 4px solid #007bff; }
    .category-deal { border-top: 4px solid #28a745; }

    .deal-badge {
        position: absolute; top: -12px; left: 1rem; background: #007bff; color: white; padding: 2px 10px; border-radius: 12px; font-size: 0.75rem; font-weight: bold; box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .category-badge { background: #28a745; }

    .result-card h4 { margin: 0.5rem 0 0.5rem 0; font-size: 1.1rem; }
    .highlight { color: #333; text-decoration: none; transition: color 0.2s; }
    .highlight:hover { color: #0056b3; text-decoration: underline; }
    
    .explanation { color: #444; line-height: 1.5; margin-bottom: 1rem; font-size: 0.95rem; }
    .reward-estimate { background: #f8f9fa; color: #333; display: inline-block; padding: 0.4rem 0.8rem; border-radius: 6px; font-size: 0.9rem; border: 1px solid #eee; }

    .reasoning-box { margin-top: 1.5rem; font-size: 0.9rem; }
    .reasoning-box details { background: #f8f9fa; padding: 0.8rem; border-radius: 6px; border: 1px solid #e2e8f0; }
    .reasoning-box summary { font-weight: bold; cursor: pointer; color: #475569; }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

    .location-toggle-field {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        padding-bottom: 2px;
    }
    .toggle-container {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        cursor: pointer;
        user-select: none;
    }
    .toggle-container input {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
    }
    .toggle-slider {
        position: relative;
        display: inline-block;
        width: 36px;
        height: 20px;
        background-color: #ccc;
        border-radius: 20px;
        transition: 0.3s;
    }
    .toggle-slider:before {
        position: absolute;
        content: "";
        height: 14px;
        width: 14px;
        left: 3px;
        bottom: 3px;
        background-color: white;
        border-radius: 50%;
        transition: 0.3s;
    }
    .toggle-container input:checked + .toggle-slider {
        background-color: #2563eb;
    }
    .toggle-container input:checked + .toggle-slider:before {
        transform: translateX(16px);
    }
    .toggle-text {
        font-size: 0.9rem;
        font-weight: 600;
        color: #1e293b;
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
    }
    .beta-tag {
        background: #e0e7ff;
        color: #3730a3;
        font-size: 0.7rem;
        padding: 1px 6px;
        border-radius: 8px;
        font-weight: bold;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
    .loc-badge {
        font-size: 0.75rem;
        padding: 2px 8px;
        border-radius: 12px;
        font-weight: 500;
        width: fit-content;
        margin-top: 4px;
    }
    .loc-badge.active {
        background: #dcfce7;
        color: #166534;
    }
    .loc-badge.loading {
        background: #fef3c7;
        color: #92400e;
    }
    .loc-error-msg {
        font-size: 0.8rem;
        color: #dc2626;
        margin-top: 2px;
    }
    .deal-location-badge {
        margin-top: 0.8rem;
        margin-bottom: 0.3rem;
        background: #e0f2fe;
        color: #0369a1;
        font-size: 0.85rem;
        font-weight: 600;
        padding: 4px 10px;
        border-radius: 6px;
        display: inline-block;
        border: 1px solid #bae6fd;
    }
</style>